/**
 * How a notification reads.
 *
 * The wording is built here and nowhere else, so the bell panel and the full
 * page cannot drift apart. Two rules the copy has to keep:
 *
 * 1. Nothing assumes `task` or `meeting` is present. The server returns
 *    `resolved: false` when the recipient can no longer open the thing, and
 *    that row shows what it can and is not a link.
 * 2. A notification never names what a booking was booked over. The
 *    `clashesWithOwnDiary` flag says only that it overlaps something already in
 *    the reader's own diary, which is a fact about their calendar and nobody
 *    else's.
 */
import { Link } from 'react-router'
import type { AtlasNotification, NotificationKind } from '../api/types'
import { formatMeetingWhen, formatDate, relativeTime } from '../lib/dates'
import { cx } from './ui'

const VERB: Record<NotificationKind, string> = {
  TASK_ASSIGNED: 'assigned you',
  TASK_UNASSIGNED: 'took this off you',
  TASK_DUE_CHANGED: 'changed the due date on',
  MEETING_BOOKED: 'booked you into',
  MEETING_CHANGED: 'changed',
  MEETING_CANCELLED: 'cancelled',
}

/** Where a row points. Null when there is nothing this person may open. */
export function notificationHref(n: AtlasNotification): string | null {
  if (!n.resolved) return null
  if (n.task) return `/t/${n.task.id}`
  if (n.meeting) return `/m/${n.meeting.id}`
  return null
}

function readsAsClash(n: AtlasNotification): boolean {
  if (n.kind !== 'MEETING_BOOKED') return false
  const meta = n.meta as { clashesWithOwnDiary?: boolean } | null
  return Boolean(meta?.clashesWithOwnDiary)
}

/** The one line under the title: where the thing lives and when it is. */
function context(n: AtlasNotification): string | null {
  if (n.meeting) {
    const when = formatMeetingWhen(n.meeting.startsAt, n.meeting.endsAt)
    const company = n.meeting.company?.shortName ?? n.meeting.company?.name
    return company ? `${when} · ${company}` : when
  }
  if (n.task) {
    const where = n.task.project?.name ?? n.task.company?.shortName ?? n.task.company?.name ?? null
    const due = n.task.dueDate ? `Due ${formatDate(n.task.dueDate)}` : null
    return [where, due].filter(Boolean).join(' · ') || null
  }
  return null
}

export function NotificationRow({
  notification,
  onOpen,
}: {
  notification: AtlasNotification
  onOpen?: () => void
}) {
  const href = notificationHref(notification)
  const unread = notification.readAt === null
  const actor = notification.actor?.name ?? 'Someone'
  const clash = readsAsClash(notification)
  const detail = context(notification)

  const inner = (
    <>
      <span
        aria-hidden="true"
        className={cx(
          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
          unread ? 'bg-brand-600 dark:bg-brand-400' : 'bg-transparent',
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-slate-700 dark:text-slate-300">
          <span className="font-medium text-slate-900 dark:text-slate-100">{actor}</span>{' '}
          {VERB[notification.kind]}{' '}
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {notification.title}
          </span>
        </span>
        {detail && (
          <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{detail}</span>
        )}
        {clash && (
          <span className="mt-1 block text-xs font-medium text-amber-700 dark:text-amber-400">
            This overlaps something already in your diary.
          </span>
        )}
        {!notification.resolved && (
          <span className="mt-0.5 block text-xs text-slate-400 dark:text-slate-500">
            You no longer have access to this.
          </span>
        )}
        <span className="mt-1 block text-xs text-slate-400 dark:text-slate-500">
          {relativeTime(notification.createdAt)}
        </span>
      </span>
    </>
  )

  const shared = cx(
    'flex w-full gap-2.5 px-3 py-2.5 text-left transition-colors',
    unread ? 'bg-brand-50/50 dark:bg-brand-900/10' : '',
  )

  // A row with nothing to open is a paragraph, not a dead link.
  if (!href) {
    return <div className={shared}>{inner}</div>
  }

  return (
    <Link
      to={href}
      onClick={onOpen}
      className={cx(shared, 'hover:bg-slate-50 dark:hover:bg-slate-800/60')}
    >
      {inner}
    </Link>
  )
}

export function NotificationList({
  notifications,
  onOpen,
}: {
  notifications: AtlasNotification[]
  onOpen?: (notification: AtlasNotification) => void
}) {
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
      {notifications.map((n) => (
        <li key={n.id}>
          <NotificationRow notification={n} onOpen={onOpen ? () => onOpen(n) : undefined} />
        </li>
      ))}
    </ul>
  )
}
